import FireworksComponent from "ags/components/fireworks-component";
import FireworksExplosionComponent, { FireworkSequenceConfig } from "ags/components/fireworks-explosion-component";
import PickBonusComponent from "ags/components/pick-bonus-component";
import { JackpotTiers, TierNames } from "ags/names";
import { CloseCommand } from "slotworks/commands/comms/close-command";
import Components from "slotworks/components/components";
import PromptContent from "slotworks/components/prompt/prompt-content";
import GameState from "slotworks/model/game-state";
import PickMatchRecord from "slotworks/model/gameplay/records/pick-match-record";
import PickRequestPayload from "slotworks/model/gameplay/requests/pick-request-payload";
import commsManager from "slotworks/server/comms-manager";
import { Services } from "slotworks/services/services";
import smartUiFlags, { SmartUIFlag } from "slotworks/smart-ui/smart-ui-flags";
import soundManager from "slotworks/sound/sound-manager";
import AbstractBonus from "slotworks/state-machine/standard/states/abstract-bonus";
import Timer from "slotworks/utils/timer";

export default class PickBonusState extends AbstractBonus {

    protected pickBonusComponent: PickBonusComponent;

    private choice: Map<JackpotTiers, number[]>;

    public onEnter() {

        gameWrapper.enableFeatureMode();

        this.pickBonusComponent = new PickBonusComponent();

        this.setupChoices();

        FireworksExplosionComponent.start(this.getFireworksConfig()).then(() => {
            this.startPickBonus();

            super.onEnter();
        });
    }

    protected setupChoices() {
        this.choice = new Map<JackpotTiers, number[]>();

        this.choice.set(JackpotTiers.MINI, []);
        this.choice.set(JackpotTiers.MAJOR, []);
        this.choice.set(JackpotTiers.MINOR, []);
        this.choice.set(JackpotTiers.GRAND, []);
    }

    protected startPickBonus() {
        this.pickBonusComponent.start();

        const gameplay = GameState.getCurrentGame();
        const record = gameplay.getCurrentRecord();

        const isRecovery = gameplay.isRecovered;

        const isInPickBonus = record instanceof PickMatchRecord;

        if (isRecovery && isInPickBonus) {
            const records = record.parent.children as PickMatchRecord[];

            let index = 0;
            for (const pickRecord of records) {
                this.processRecord(pickRecord, index);
                index++;
            }
        }

        this.pickBonusComponent.onCoinClicked.add((coin: number, pickNumber: number) => this.onCoinClicked(coin, pickNumber));
    }

    protected getFireworksConfig(): FireworkSequenceConfig {
        return null;
    }

    protected resetTrail() {
        FireworksComponent.reset();
    }

    protected onPickResult(index: number, result: JackpotTiers, complete: boolean) {
        const tier = result;

        this.choice.get(tier).push(index);

        this.pickBonusComponent.revealCoin(index, tier);

        if (this.choice.get(tier).length > 1) {
            this.pickBonusComponent.highlightCoins(this.choice.get(tier));
        }

        if (complete) {
            this.pickComplete(tier);
        }
    }

    protected pickComplete(tier: JackpotTiers) {
        this.pickBonusComponent.end();
        soundManager.customEvent("end_prog_lower");

        Timer.setTimeout(() => {
            soundManager.customEvent("end_prog_win");
            soundManager.customEvent(`prog_win_${tier}`);
            this.showPrompt(tier);
        }, 2000);
    }

    private onCoinClicked(index: number, pickNumber: number) {
        const pick = new PickRequestPayload();
        pick.pick = index;

        commsManager.request(pick).then(() => {

            if (GameState.getCurrentGame().getCurrentRecord()) {
                GameState.getCurrentGame().nextRecord();

                const pickRecord = GameState.getCurrentGame().getCurrentRecord() as PickMatchRecord;

                this.processRecord(pickRecord, pickNumber, true);
            } else {
                this.onServerError();
            }
        });
    }

    private onServerError() {
        // Let the wrapper take over
        smartUiFlags.set(SmartUIFlag.PROMPT, true);
        gameWrapper.disableFeatureMode();
        gameWrapper.setIdle();
    }

    private processRecord(pickRecord: PickMatchRecord, pickNumber: number, userPick: boolean = false) {
        const prize = TierNames.get(pickRecord.value as string);

        if (userPick) {
            // Only play sound if this was a user interaction, not an auto pick on recovery
            soundManager.customEvent(`pick${pickNumber}`);
            if (prize === JackpotTiers.GRAND) {
                soundManager.customEvent("grand_reveal");
            }

            if (this.choice.get(prize).length === 2) {
                soundManager.customEvent("reveal");
            }
        }

        this.onPickResult(pickRecord.index, prize, pickRecord.isPrize);
    }

    private showPrompt(tier: JackpotTiers) {

        const record = GameState.getCurrentGame().getCurrentRecord();
        const amountWon = record.cashWon;

        const promptContent = new PromptContent(["jackpot_" + tier]);
        const value = Services.currency.format(amountWon, false, false);
        promptContent.setText("value", value);

        Components.prompt.showPrompt(promptContent).then(() => {
            this.pickBonusComponent.hide();

            this.resetTrail();

            CloseCommand().then(() => {

                GameState.getCurrentGame().getCurrentRecordParent().setBonusPlayed("pick");
                GameState.getCurrentGame().resetToParentRecord();

                this.complete();
            });
        });
    }
}
